import React, { useEffect, useRef, useState } from "react";

import moment from "moment";

import {
  ArrayUtils,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFarmLandUseChanges,
  useGetFootprintingFarmPrimaryDataById,
  useGetLandUseChanges,
  useKeyedFormList,
  useMutateFarmLandUseChanges,
  useMutateFootprintingFarmPrimaryData,
  useTranslation,
} from "utils";

import {
  ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION,
  ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED,
  ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE,
  FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION,
  FARM_FOOTPRINTING_HAS_LAND_CHANGED,
  FARM_FOOTPRINTING_OVERALL_SOIL_TYPE,
  PRACTICE_SLUG,
  UploadableLandUseChange,
  ZERO_UUID,
} from "models";

import {
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Radio,
  Select,
  Tooltip,
  Typography,
} from "components";

interface FarmLandUseFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const landUseChangePlaceholder: UploadableLandUseChange = {
  id: undefined,
  yearOfConversion: null,
  areaConvertedHa: null,
  nextLandUseChangeId: null,
  previousLandUseChangeId: null,
};

const FarmLandUseForm = ({ readOnly, farmId, year, primaryDataId }: FarmLandUseFormProps) => {
  const { t } = useTranslation("footprintingFarm");
  const formRef = useRef<FormInstance>(null);
  const selectGroupName = "footprinting-farm-land-use-change";
  const soilTypeSelectGroupName = "footprinting-farm-soil-select-soil-type";

  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const startYear = carbonProgramQuery.data?.farmLevelFootprinting?.startYear;
  const isStartYear = startYear === year;

  const hasLandChangedFormRefKey = "hasLandChanged";
  const [hasLandChanged, setHasLandChanged] = useState<ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null>(null);
  const [arableLandActive, setArableLandActive] = useState<boolean | null>(null);
  const [overallSoilType, setOverallSoilType] = useState<ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null>(null);
  const [averageSoilSaturation, setAverageSoilSaturation] = useState<ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null>(null);

  const yearRange = isStartYear ? 20 : 1;
  const landUseChangeQuery = useGetLandUseChanges();
  const farmPrimaryDataQuery = useGetFootprintingFarmPrimaryDataById(primaryDataId ?? "");
  const farmLandUseChangeUsageQuery = useGetFarmLandUseChanges(farmId, {
    year,
    range: yearRange,
  });
  const isLoading = [landUseChangeQuery, farmPrimaryDataQuery, carbonProgramQuery, farmLandUseChangeUsageQuery].some(queryIsLoading);
  const keyedLandUseChanges = useKeyedFormList<UploadableLandUseChange>(formRef, { isReady: !isLoading });

  const { mutate: mutatePD } = useMutateFootprintingFarmPrimaryData(
    farmId,
    year,
    {
      year,
      farmId,
      hasLandChanged,
      arableLandActive,
      // Soil data only applies to arable land, so it is cleared otherwise
      overallSoilType: arableLandActive ? overallSoilType : null,
      averageSoilSaturation: arableLandActive ? averageSoilSaturation : null,
    },
    primaryDataId
  );

  const { mutate: mutateFarmLandUseChangeUsage } = useMutateFarmLandUseChanges(
    farmId,
    year,
    yearRange,
    {
      farmId,
      selectedLandUseChange:
        hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES
          ? keyedLandUseChanges.payload
              .filter((change) => !!change.previousLandUseChangeId && !!change.nextLandUseChangeId)
              .map((change) => ({
                id: change.id,
                yearOfConversion: change.yearOfConversion,
                areaConvertedHa: change.areaConvertedHa,
                nextLandUseChangeId: change.nextLandUseChangeId,
                previousLandUseChangeId: change.previousLandUseChangeId,
              }))
          : [],
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        mutateFarmLandUseChangeUsage();
        mutatePD({});
      }
    },
    [isLoading, mutateFarmLandUseChangeUsage, mutatePD, primaryDataId, readOnly]
  );

  useEffect(() => {
    if (!isLoading && farmLandUseChangeUsageQuery.isFetchedAfterMount) {
      const initialLUCs: UploadableLandUseChange[] = (farmLandUseChangeUsageQuery.data ?? [])
        .filter((luc) => luc.id)
        .map((luc) => ({
          id: luc.id,
          areaConvertedHa: luc.areaConvertedHa,
          yearOfConversion: luc.yearOfConversion,
          previousLandUseChangeId: luc.previousLandUseChangeId,
          nextLandUseChangeId: luc.nextLandUseChangeId,
        }));

      // We're ok to prefill a placeholder even if they have `NO` or `UNKNOWN`
      // because the mutate function payload checks the state of `hasLandChanged`
      // and will just send `[]` anyway
      keyedLandUseChanges.actions.hydrate(
        initialLUCs.length > 0 ? initialLUCs : [{ ...landUseChangePlaceholder, yearOfConversion: isStartYear ? null : year }]
      );
      setHasLandChanged(farmPrimaryDataQuery.data?.hasLandChanged ?? null);
      setArableLandActive(farmPrimaryDataQuery.data?.arableLandActive ?? null);
      setOverallSoilType(farmPrimaryDataQuery.data?.overallSoilType ?? null);
      setAverageSoilSaturation(farmPrimaryDataQuery.data?.averageSoilSaturation ?? null);

      formRef.current?.setFieldValue(hasLandChangedFormRefKey, farmPrimaryDataQuery.data?.hasLandChanged ?? null);
      // Only rehydrated entries are validated; entries created by switching to `YES` later stay neutral until touched
      keyedLandUseChanges.actions.requestRehydrationValidation();
    }
  }, [
    carbonProgramQuery.data,
    farmLandUseChangeUsageQuery.data,
    farmLandUseChangeUsageQuery.isFetchedAfterMount,
    farmPrimaryDataQuery.data,
    isLoading,
    isStartYear,
    year,
    keyedLandUseChanges.actions,
  ]);

  if (!primaryDataId || isLoading) return <PageSpin />;

  const questionText = isStartYear ? t("landUseForm.question.general") : t("landUseForm.question.followingYearQuestion");

  const questionOptions = isStartYear
    ? [
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO, label: t("landUseForm.radioButton.no") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES, label: t("landUseForm.radioButton.yes") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN, label: t("landUseForm.radioButton.dontKnow") },
      ]
    : [
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO, label: t("landUseForm.radioButton.noFollowingYear") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES, label: t("landUseForm.radioButton.yes") },
      ];

  const [nextLandUses, previousLandUses] = ArrayUtils.partition(landUseChangeQuery.data?.filter((pluc) => pluc.id !== ZERO_UUID) ?? [], (pluc) =>
    ["cropland", "pasture"].includes(pluc.translationSlug)
  );

  return (
    <Flex vertical gap={16}>
      <Card border dataTestId="footprinting-farm-arable-land-card" className="padding-200">
        <Flex vertical gap={16}>
          <Typography variant="paragraph-l">{t("landUseForm.arableLand.title")}</Typography>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" className="flex-1">
              {t("landUseForm.arableLand.description")}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-farm-arable-land-radio"
              onChange={({ target }) => setArableLandActive(target.value)}
              value={arableLandActive}
              optionType="button"
              options={[
                { label: t("landUseForm.arableLand.yes"), value: true },
                { label: t("landUseForm.arableLand.no"), value: false },
              ]}
            />
          </Flex>
          {arableLandActive && (
            <Form>
              <Flex vertical gap={16}>
                <Select
                  disabled={readOnly}
                  dataTestId={soilTypeSelectGroupName}
                  label={t("soilForm.soilTypeLabel")}
                  tooltip={t("soilForm.soilTypeLabelTooltip")}
                  rules={[{ required: true, message: "" }]}
                  initialValue={overallSoilType}
                  value={overallSoilType}
                  onChange={(v) => setOverallSoilType(v)}
                  dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                >
                  {Object.values(FARM_FOOTPRINTING_OVERALL_SOIL_TYPE).map((type) => (
                    <Option
                      disabled={readOnly}
                      key={`${soilTypeSelectGroupName}-${type}`}
                      data-testid={`${soilTypeSelectGroupName}-${type}`}
                      label={t(`soilForm.soilTypeOptions.${type}`)}
                      value={type}
                    >
                      {t(`soilForm.soilTypeOptions.${type}`)}
                    </Option>
                  ))}
                </Select>

                <Flex align="center" gap={16} justify="space-between">
                  <Typography variant="paragraph-m" className="flex-1">
                    {t("soilForm.soilSaturationLabel")}
                    <span className="required-asterisk">*</span>
                    <Tooltip title={t("soilForm.soilSaturationLabelTooltip")}>
                      <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
                    </Tooltip>
                  </Typography>
                  <KlimRadioGroup
                    required
                    vertical={false}
                    disabled={readOnly}
                    type="tertiary"
                    size="large"
                    dataTestId="footprinting-farm-soil-saturation-radio"
                    onChange={({ target }) => setAverageSoilSaturation(target.value)}
                    value={averageSoilSaturation}
                    optionType="button"
                    options={[FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION.HIGH, FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION.LOW].map((saturation) => ({
                      label: t(`soilForm.soilSaturationOptions.${saturation}`),
                      value: saturation,
                    }))}
                  />
                </Flex>
              </Flex>
            </Form>
          )}
        </Flex>
      </Card>
      <Card border dataTestId="footprinting-farm-land-use-chages-form" className="padding-200">
        <Flex vertical gap={16}>
          <Typography variant="paragraph-l">{questionText}</Typography>
          <Form ref={formRef} preserve={false}>
            <Flex vertical gap={32}>
              <Form.Item name={hasLandChangedFormRefKey} initialValue={hasLandChanged} rules={[{ message: t("landUseForm.radioButton.error") }]}>
                <Radio.Group onChange={(e) => setHasLandChanged(e.target.value)} value={hasLandChanged}>
                  <Flex vertical gap={16} justify="space-between">
                    {questionOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Flex>
                </Radio.Group>
              </Form.Item>
              {hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES && (
                <>
                  {keyedLandUseChanges.entries.map((landUseChange, index) => {
                    const selectGroupWithIndex = `${selectGroupName}-${index}`;
                    const fieldNamePrefix = `${selectGroupName}-${landUseChange.clientKey}`;
                    return (
                      <Flex vertical key={landUseChange.clientKey} gap={16}>
                        <Flex justify="space-between" align="center">
                          <Typography variant="h3">{t("landUseForm.question.title") + (index + 1)}</Typography>
                          <IconButton
                            type="tertiary"
                            dataTestId={`${selectGroupWithIndex}-delete`}
                            disabled={readOnly}
                            size="small"
                            iconName="tabler/trash"
                            onClick={() => {
                              keyedLandUseChanges.actions.remove(landUseChange.clientKey);
                              if (keyedLandUseChanges.entries.length === 1) setHasLandChanged(null);
                            }}
                          />
                        </Flex>

                        <Select
                          name={`${fieldNamePrefix}-previous`}
                          allowClear
                          disabled={readOnly}
                          dataTestId={`${selectGroupWithIndex}-previous`}
                          className="footprinting-luc-select"
                          label={t("landUseForm.question.previousLandChange")}
                          message={t("landUseForm.radioButton.error")}
                          rules={[{ required: true }]}
                          initialValue={landUseChange.previousLandUseChangeId}
                          value={landUseChange.previousLandUseChangeId}
                          onChange={(v) => {
                            keyedLandUseChanges.actions.update(landUseChange.clientKey, { previousLandUseChangeId: v });
                          }}
                        >
                          {previousLandUses.map((pluc) => (
                            <Option
                              disabled={readOnly}
                              key={`${selectGroupWithIndex}-previous-${pluc.id}`}
                              data-testid={`${selectGroupWithIndex}-previous-${pluc.id}`}
                              value={pluc.id}
                              label={t(`${PRACTICE_SLUG.LAND_USE_CHANGES}.${pluc.translationSlug}`, { ns: "practices" }) || pluc.translationSlug}
                            >
                              {t(`${PRACTICE_SLUG.LAND_USE_CHANGES}.${pluc.translationSlug}`, { ns: "practices" }) || pluc.translationSlug}
                            </Option>
                          ))}
                        </Select>

                        <Select
                          name={`${fieldNamePrefix}-next`}
                          allowClear
                          disabled={readOnly}
                          dataTestId={`${selectGroupWithIndex}-next`}
                          className="footprinting-luc-select"
                          label={t("landUseForm.question.nextLandChange")}
                          message={t("landUseForm.radioButton.error")}
                          rules={[{ required: true }]}
                          initialValue={landUseChange.nextLandUseChangeId}
                          value={landUseChange.nextLandUseChangeId}
                          onChange={(v) => {
                            keyedLandUseChanges.actions.update(landUseChange.clientKey, { nextLandUseChangeId: v });
                          }}
                        >
                          {nextLandUses.map((pluc) => (
                            <Option
                              disabled={readOnly}
                              key={`${selectGroupWithIndex}-next-${pluc.id}`}
                              data-testid={`${selectGroupWithIndex}-next-${pluc.id}`}
                              value={pluc.id}
                              label={t(`${PRACTICE_SLUG.LAND_USE_CHANGES}.${pluc.translationSlug}`, { ns: "practices" }) || pluc.translationSlug}
                            >
                              {t(`${PRACTICE_SLUG.LAND_USE_CHANGES}.${pluc.translationSlug}`, { ns: "practices" }) || pluc.translationSlug}
                            </Option>
                          ))}
                        </Select>

                        <Flex gap={16}>
                          <Form.Item className="flex-1">
                            <DatePicker
                              name={`${fieldNamePrefix}-conversionYear`}
                              dataTestId={`${selectGroupWithIndex}-conversionYear`}
                              label={t("landUseForm.question.conversionYear")}
                              format="YYYY"
                              picker="year"
                              disabled={readOnly}
                              value={landUseChange.yearOfConversion ? moment([landUseChange.yearOfConversion]) : null}
                              initialValue={landUseChange.yearOfConversion ? moment([landUseChange.yearOfConversion]) : null}
                              onChange={(date) => {
                                const yearOfConversion = date ? date.year() : null;
                                keyedLandUseChanges.actions.update(landUseChange.clientKey, { yearOfConversion });
                              }}
                              disabledDate={(y) => (isStartYear ? y.year() < year - 20 || y.year() > year : y.year() !== year)}
                              rules={[{ required: true, message: t("landUseForm.question.landUseChangeYearQuestionError") }]}
                            />
                          </Form.Item>

                          <Form.Item className="flex-1">
                            <InputNumber
                              name={`${fieldNamePrefix}-areaConverted`}
                              dataTestId={`${selectGroupWithIndex}-areaConverted`}
                              label={t("landUseForm.question.areaConverted")}
                              suffix="Ha"
                              disabled={readOnly}
                              value={landUseChange.areaConvertedHa}
                              initialValue={landUseChange.areaConvertedHa}
                              onChange={(v) => keyedLandUseChanges.actions.update(landUseChange.clientKey, { areaConvertedHa: v })}
                              rules={[
                                { required: true, message: t("landUseForm.question.landUseChangeAreaQuestionError") },
                                { type: "number" },
                                {
                                  validator: (_: unknown, v: number) =>
                                    isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject(),
                                  message: t("landUseForm.question.landUseChangeAreaQuestionError"),
                                },
                              ]}
                            />
                          </Form.Item>
                        </Flex>
                      </Flex>
                    );
                  })}
                  <Button
                    type="text"
                    disabled={readOnly}
                    onClick={() => {
                      keyedLandUseChanges.actions.add(landUseChangePlaceholder);
                    }}
                  >
                    <Icon iconName="klim/plus" className="icon inline" /> {t("landUseForm.add")}
                  </Button>
                </>
              )}
            </Flex>
          </Form>
        </Flex>
      </Card>
    </Flex>
  );
};

FarmLandUseForm.Key = "farm.landUse";
export { FarmLandUseForm };
